import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { getOptimalConfig } from '../../lib/calculations';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { formatPrice, formatNumber, formatCurrencyCompact, formatPercent, pnlColorClass } from '../../lib/formatters';
import { CheckCircle2, TrendingUp, Layers } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';

export function RecommendedConfig() {
  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const optimal = useMemo(() => getOptimalConfig(assumptions), [assumptions]);
  const portfolio = usePortfolioPnL();

  const portfolioPnl = portfolio.portfolioPnLSplit;
  const totalUnits = Math.round(portfolio.totalS2UnitsSplit);

  return (
    <div className="card card-accent-teal">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Portfolio summary */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers size={18} className={portfolioPnl >= 0 ? 'text-metric-positive' : 'text-metric-negative'} />
            <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Current Portfolio</h3>
            <InfoButton helpKey="portfolioPnL" size={13} />
          </div>
          <p className="text-micro text-muted mb-3">{packages.length} packages, fixed costs shared, split demand</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted mb-1">Monthly P&L</div>
              <div className={`text-xl font-bold font-mono ${pnlColorClass(portfolioPnl)}`}>
                {formatCurrencyCompact(portfolioPnl)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Annual P&L</div>
              <div className={`text-xl font-bold font-mono ${pnlColorClass(portfolioPnl * 12)}`}>
                {formatCurrencyCompact(portfolioPnl * 12)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Total S2 Units</div>
              <div className="text-lg font-bold text-primary font-mono">{formatNumber(totalUnits)}</div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Packages</div>
              <div className="flex flex-wrap gap-1">
                {portfolio.packages.map((p, i) => {
                  const margin = p.contributionMarginSplit;
                  return (
                    <span key={i} className={`text-micro px-1.5 py-0.5 rounded font-medium ${margin >= 0 ? 'bg-metric-positive/10 text-metric-positive' : 'bg-metric-negative/10 text-metric-negative'}`}>
                      {packages[i]?.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Best single product */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 size={18} className="text-chart-teal" />
            <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Best Single Product</h3>
            <InfoButton helpKey="recommendedConfig" size={13} />
          </div>
          <p className="text-micro text-muted mb-3">Highest standalone P&L (full fixed costs on one product)</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted mb-1">Price</div>
              <div className="text-xl font-bold text-primary font-mono">{formatPrice(optimal.price)}</div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Features</div>
              <div className="text-xl font-bold text-primary font-mono">{optimal.featureCount} of 7</div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Standalone P&L</div>
              <div className={`text-lg font-bold font-mono ${pnlColorClass(optimal.monthlyPnL)}`}>
                {formatCurrencyCompact(optimal.monthlyPnL)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Demand</div>
              <div className="text-lg font-bold text-primary font-mono">{formatPercent(optimal.adjustedChoiceRate)}</div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1">
            {optimal.features.map(f => (
              <span key={f} className="inline-flex items-center gap-1 text-micro px-1.5 py-0.5 rounded-full bg-chart-teal/10 text-chart-teal font-medium">
                <TrendingUp size={8} /> {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Portfolio vs Single Product Delta */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Portfolio vs. Best Single Product:</span>
          {(() => {
            const delta = portfolioPnl - optimal.monthlyPnL;
            return (
              <span className={`font-mono font-semibold ${pnlColorClass(delta)}`}>
                {delta >= 0 ? '+' : ''}{formatCurrencyCompact(delta)}/mo
                {delta > 0 ? ' — portfolio outperforms' : delta < 0 ? ' — single product outperforms' : ' — equal'}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
